import React from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Avatar,
  IconButton,
  Button,
  Divider,
} from '@chakra-ui/react';
import { ThumbsUp, CaretDown, CaretUp, ChatText } from '@phosphor-icons/react';
import ReplyInput from './ReplyInput';
import ReplyList from './ReplyList';

const CommentItem = ({
  comment,
  user,
  siteId,
  onLike,
  onReply,
  replyTo,
  handleReplyClick,
  collapsedThreads,
  toggleThread,
  commentRef,
  isTopLevel = false,
  // Removed borderColor prop as it's derived from theme now
}) => {
  // Removed useColorModeValue hooks, using semantic tokens directly below

  return (
    <Box
      id={`comment-${comment.id}`}
      ref={commentRef}
      position="relative"
      {...(isTopLevel ? {
        borderWidth: "1px",
        borderColor: "border.default", // Use semantic token
        bg: "bg.surface", // Use semantic token
        borderRadius: "md",
        boxShadow: "sm", // Use theme shadow token
        p: { base: 4, md: 5 },
        _before: { // Keep inner border style if desired
          content: '""',
          position: 'absolute',
          top: '2px',
          left: '2px',
          right: '2px',
          bottom: '2px',
          borderRadius: 'md',
          border: '1px solid',
          borderColor: 'border.default', // Use semantic token
          opacity: 0.3,
          pointerEvents: 'none'
        }
      } : {
        borderLeft: "2px solid",
        borderColor: "border.light", // Use semantic token for thread line
        pl: { base: 4, md: 5 },
        ml: { base: 2, md: 3 },
        mt: 4
      })}
    >
      <HStack align="start" spacing={{ base: 3, md: 4 }} flexWrap="wrap">
        <Avatar 
          size={{ base: "sm", md: "md" }} 
          name={comment.authorName} 
          bg="ancient.terracotta"
          color="white"
        />
        <VStack align="start" spacing={2} width="full">
          <HStack 
            align="center" 
            spacing={{ base: 2, md: 3 }}
            flexWrap="wrap"
            width="full"
          >
            <Text 
              textStyle="carved"
              fontWeight="600"
              fontSize={{ base: "sm", md: "md" }}
              color="ancient.terracotta"
            >
              {comment.authorName}
            </Text>
            <Text
              fontSize="sm"
              color="text.muted" // Use semantic token
              textStyle="papyrus"
            >
              {comment.createdAt?.seconds ?
                new Date(comment.createdAt.seconds * 1000).toLocaleDateString() :
                new Date().toLocaleDateString()}
            </Text>
            <HStack spacing={{ base: 2, md: 3 }}>
              <IconButton
                size={{ base: "xs", md: "sm" }}
                variant={comment.likes?.includes(user?.uid) ? 'relief' : 'tablet'}
                icon={<ThumbsUp weight={comment.likes?.includes(user?.uid) ? "fill" : "regular"} />}
                aria-label="Like"
                onClick={() => onLike(comment.id, comment.likes?.includes(user?.uid))}
              />
              <Text fontSize="sm" color="text.muted">{comment.likes?.length || 0}</Text> {/* Use semantic token */}
              <Button
                size={{ base: "xs", md: "sm" }}
                variant="tablet" // Assuming 'tablet' variant is defined in theme
                leftIcon={<ChatText />}
                onClick={() => handleReplyClick(comment.id)}
              >
                Reply
              </Button>
              {comment.replies?.length > 0 && (
                <IconButton
                  size={{ base: "xs", md: "sm" }}
                  variant="tablet"
                  icon={collapsedThreads.has(comment.id) ? 
                    <CaretDown size={16} /> : 
                    <CaretUp size={16} />
                  }
                  aria-label={collapsedThreads.has(comment.id) ? "Expand" : "Collapse"}
                  onClick={(e) => toggleThread(comment.id, e)}
                />
              )}
            </HStack>
          </HStack>
          
          <Text 
            fontSize={{ base: "sm", md: "md" }}
            className="ancient-text"
          >
            {comment.text}
          </Text>
          
          {comment.photoUrl && (
            <Box 
              mt={2} 
              cursor="pointer"
              onClick={() => onReply.setSelectedImage(comment.photoUrl)}
              maxW={{ base: "150px", md: "200px" }}
              borderRadius="md"
              overflow="hidden"
              boxShadow="sm" // Use theme shadow token
              position="relative"
              _after={{ // Keep inner border style if desired
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: '1px solid',
                borderColor: 'border.default', // Use semantic token
                borderRadius: 'md',
                opacity: 0.3,
                pointerEvents: 'none'
              }}
            >
              <img 
                src={comment.photoUrl} 
                alt={`${comment.authorName}'s comment`} 
                style={{ 
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }} 
              />
            </Box>
          )}
          
          {comment.replies?.length > 0 && !collapsedThreads.has(comment.id) && (
            <Divider className="ancient-divider" />
          )}
          
          <ReplyList
            replies={comment.replies}
            parentId={comment.id}
            isCollapsed={collapsedThreads.has(comment.id)}
            onLike={onLike}
            onReply={onReply}
            replyTo={replyTo}
            user={user}
            siteId={siteId}
            handleReplyClick={handleReplyClick}
            collapsedThreads={collapsedThreads}
            toggleThread={toggleThread}
            // Removed borderColor prop pass-through
          />

          {replyTo === comment.id && (
            <ReplyInput
              user={user}
              onSubmit={onReply.addReply}
              siteId={siteId}
              commentId={comment.id}
            />
          )}
        </VStack>
      </HStack>
    </Box>
  );
};

export default CommentItem;
